/**
 * Passkey (WebAuthn) browser helpers.
 *
 * Thin orchestration around @simplewebauthn/browser + our GraphQL mutations.
 * The backend hands us option JSON as a string; we parse it, run the WebAuthn
 * ceremony in the browser (Touch ID / Windows Hello / security key / phone),
 * then send the signed response back as a string for verification.
 *
 * Shared by the /security page (enroll + manage) and UnifiedLoginModal (login).
 */
import {
    startRegistration,
    startAuthentication,
    browserSupportsWebAuthn,
    platformAuthenticatorIsAvailable,
} from "@simplewebauthn/browser";

export { browserSupportsWebAuthn, platformAuthenticatorIsAvailable };

/** Apollo `useMutation` tuple's mutate function. */
type MutateFn = (opts?: any) => Promise<any>;

/**
 * Enroll a new passkey for the currently-authenticated user.
 * @returns the created credential ({ id, name, createdAt }).
 */
export async function enrollPasskey(
    generateOptions: MutateFn,
    verifyRegistration: MutateFn,
    name: string
): Promise<{ id: string; name: string }> {
    const { data } = await generateOptions();
    const optionsJSON = JSON.parse(data.generatePasskeyRegistrationOptions);

    const attResp = await startRegistration({ optionsJSON });

    const res = await verifyRegistration({
        variables: { input: { responseJSON: JSON.stringify(attResp), name } },
    });
    return res.data.verifyPasskeyRegistration;
}

/**
 * Authenticate with a passkey (usernameless). The browser offers any discoverable
 * passkey bound to this site.
 * @returns the JWT auth token.
 */
export async function loginWithPasskey(
    generateOptions: MutateFn,
    verifyAuthentication: MutateFn
): Promise<string> {
    const { data } = await generateOptions();
    const optionsJSON = JSON.parse(data.generatePasskeyAuthenticationOptions);

    const authResp = await startAuthentication({ optionsJSON });

    const res = await verifyAuthentication({
        variables: { input: { responseJSON: JSON.stringify(authResp) } },
    });
    const token = res?.data?.verifyPasskeyAuthentication?.token;
    if (!token) throw new Error("Passkey login failed");
    return token;
}

/**
 * Step-up with a passkey (two-step verification, 2026-09-26): prove it's you again in an
 * already-signed-in session. The options only allow the caller's OWN credentials; the backend
 * verifies the assertion against them and returns a RE-ISSUED token (adopt it).
 * @returns the step-up result ({ token, aal, amr }).
 */
export async function stepUpWithPasskeyCeremony(
    getOptions: MutateFn,
    verifyStepUp: MutateFn
): Promise<{ token: string; aal: number; amr: string[] }> {
    const { data } = await getOptions();
    const optionsJSON = JSON.parse(data.stepUpPasskeyOptions);

    const authResp = await startAuthentication({ optionsJSON });

    const res = await verifyStepUp({ variables: { response: JSON.stringify(authResp) } });
    const result = res?.data?.stepUpWithPasskey;
    if (!result?.token) throw new Error("Your passkey could not be checked. Please try again.");
    return result;
}

/**
 * Map raw WebAuthn ceremony errors to friendly copy. The most common is the user
 * cancelling the system prompt (NotAllowedError / AbortError).
 */
export function friendlyPasskeyError(err: any): string {
    const name = err?.name || "";
    if (name === "NotAllowedError" || name === "AbortError") {
        return "Passkey prompt was cancelled or timed out.";
    }
    if (name === "InvalidStateError") {
        return "A passkey for this device is already registered.";
    }
    return err?.message || "Something went wrong with the passkey.";
}
